/* eslint-disable react-refresh/only-export-components --
   This module is an intentional single source of truth that colocates the pure,
   testable `…BadgeProps` resolvers with their thin `<…Badge>` render components.
   Fast-refresh granularity is irrelevant for these stateless helpers. */
import type { TFunction } from "i18next";
import { useTranslation } from "react-i18next";
import { Badge, type BadgeVariant } from "@/components/ui/Badge";

/**
 * Admin enum → semantic `Badge` mapping helpers (TASK-3881).
 *
 * Across the admin panels, enum values (tenant status, user role, billing
 * status, alert severity, tenant health) previously rendered inconsistently —
 * sometimes as plain grey text. This module is the single source of truth for
 * mapping each domain enum value onto an existing `Badge` variant + a localized
 * label, so the rule "enums are never plain text" can be applied everywhere.
 *
 * Design:
 * - Each enum has a pure `…BadgeProps(value, t)` resolver returning
 *   `{ variant, label }` so it is trivially unit-testable without React.
 * - Each enum also has a thin `<…Badge value={…} />` component that resolves
 *   with the active `t` and renders the existing `@/components/ui/Badge` — no
 *   new badge component, no new variants.
 * - Unknown / unmapped values never crash and never render blank: they fall
 *   back to {@link NEUTRAL_VARIANT} with the raw value as the label.
 *
 * The Badge variant union (`@/components/ui/Badge`) is fixed:
 *   draft | planned | in_progress | completed | failed | pending
 * Mappings below only reference those variants.
 */

/** Resolved badge inputs: a fixed Badge variant plus a display label. */
export interface BadgeProps {
  variant: BadgeVariant;
  label: string;
}

/**
 * Neutral fallback variant used when a value is not in the enum's map. Grey,
 * low-emphasis — signals "unknown" without implying success/failure.
 */
const NEUTRAL_VARIANT: BadgeVariant = "draft";

/**
 * Generic resolver: look up `value` in `map`, localize its label via
 * `t(`${i18nPrefix}.${key}`)`, and fall back to {@link NEUTRAL_VARIANT} + the
 * raw value for anything unmapped. The i18n `defaultValue` is the raw value so
 * a missing translation key still renders the value rather than the key.
 */
function resolveBadge(
  value: string | null | undefined,
  map: Record<string, { variant: BadgeVariant; key: string }>,
  i18nPrefix: string,
  t: TFunction,
): BadgeProps {
  const raw = value == null ? "" : String(value).trim();
  const entry = raw ? map[raw] : undefined;
  if (!entry) {
    return { variant: NEUTRAL_VARIANT, label: raw };
  }
  return {
    variant: entry.variant,
    label: t(`${i18nPrefix}.${entry.key}`, { defaultValue: raw }),
  };
}

// ---------------------------------------------------------------------------
// Tenant status
// ---------------------------------------------------------------------------

/**
 * Tenant lifecycle status. Forward-looking enum: the backend currently exposes
 * `is_active` only, but the admin UI is moving to a richer status. Map both the
 * future string values and keep the placeholder shape stable for later sprints.
 */
export type TenantStatus = "active" | "suspended" | "trial" | "churned";

const TENANT_STATUS_MAP: Record<
  string,
  { variant: BadgeVariant; key: string }
> = {
  active: { variant: "completed", key: "active" },
  suspended: { variant: "failed", key: "suspended" },
  trial: { variant: "planned", key: "trial" },
  churned: { variant: "draft", key: "churned" },
};

/** Resolve `{ variant, label }` for a tenant status (pure, testable). */
export function tenantStatusBadgeProps(
  value: TenantStatus | string | null | undefined,
  t: TFunction,
): BadgeProps {
  return resolveBadge(
    value,
    TENANT_STATUS_MAP,
    "adminPanel.badges.tenantStatus",
    t,
  );
}

// ---------------------------------------------------------------------------
// User role
// ---------------------------------------------------------------------------

/** Platform user roles, from most-privileged (superadmin) to driver. */
export type UserRole =
  | "superadmin"
  | "dealer"
  | "company_admin"
  | "dispatcher"
  | "driver";

const USER_ROLE_MAP: Record<string, { variant: BadgeVariant; key: string }> = {
  superadmin: { variant: "failed", key: "superadmin" },
  super_admin: { variant: "failed", key: "superadmin" },
  dealer: { variant: "pending", key: "dealer" },
  company_admin: { variant: "planned", key: "company_admin" },
  dispatcher: { variant: "in_progress", key: "dispatcher" },
  driver: { variant: "draft", key: "driver" },
};

/** Resolve `{ variant, label }` for a user role (pure, testable). */
export function userRoleBadgeProps(
  value: UserRole | string | null | undefined,
  t: TFunction,
): BadgeProps {
  return resolveBadge(value, USER_ROLE_MAP, "adminPanel.badges.role", t);
}

// ---------------------------------------------------------------------------
// Billing status
// ---------------------------------------------------------------------------

/**
 * Billing record / invoice status. The backend `BillingRecordStatus` is
 * `draft | issued | paid | void`; the admin design brief speaks in
 * `paid | open/unpaid | overdue | draft`. Map both vocabularies so callers can
 * pass either the API value or a derived display value.
 */
export type BillingStatus =
  | "paid"
  | "open"
  | "unpaid"
  | "issued"
  | "overdue"
  | "draft"
  | "void";

const BILLING_STATUS_MAP: Record<
  string,
  { variant: BadgeVariant; key: string }
> = {
  paid: { variant: "completed", key: "paid" },
  open: { variant: "pending", key: "open" },
  unpaid: { variant: "pending", key: "open" },
  issued: { variant: "pending", key: "open" },
  overdue: { variant: "failed", key: "overdue" },
  draft: { variant: "draft", key: "draft" },
  void: { variant: "draft", key: "void" },
};

/** Resolve `{ variant, label }` for a billing status (pure, testable). */
export function billingStatusBadgeProps(
  value: BillingStatus | string | null | undefined,
  t: TFunction,
): BadgeProps {
  return resolveBadge(
    value,
    BILLING_STATUS_MAP,
    "adminPanel.badges.billingStatus",
    t,
  );
}

// ---------------------------------------------------------------------------
// Alert severity
// ---------------------------------------------------------------------------

/** Superadmin system-alert severity bucket (`AdminAlertSeverity`). */
export type AlertSeverity = "critical" | "warning" | "info";

const ALERT_SEVERITY_MAP: Record<
  string,
  { variant: BadgeVariant; key: string }
> = {
  critical: { variant: "failed", key: "critical" },
  warning: { variant: "pending", key: "warning" },
  info: { variant: "planned", key: "info" },
};

/** Resolve `{ variant, label }` for an alert severity (pure, testable). */
export function alertSeverityBadgeProps(
  value: AlertSeverity | string | null | undefined,
  t: TFunction,
): BadgeProps {
  return resolveBadge(
    value,
    ALERT_SEVERITY_MAP,
    "adminPanel.badges.alertSeverity",
    t,
  );
}

/**
 * Tailwind class groups for a full-width banner colored by alert severity
 * (TASK-3940). Kept here so the severity color language lives in one module:
 * the broadcast bell badges and the maintenance banner stay visually
 * consistent. Maps onto the same red/amber/blue families as the
 * `ALERT_SEVERITY_MAP` Badge variants:
 *   critical → red, warning → amber, info → blue.
 * Unknown values fall back to the info (blue) styling.
 */
export interface BannerSeverityStyles {
  /** Container border + background. */
  container: string;
  /** Leading icon color. */
  icon: string;
  /** Message text color. */
  text: string;
  /** Dismiss-button color + hover. */
  button: string;
}

const ALERT_SEVERITY_BANNER_MAP: Record<AlertSeverity, BannerSeverityStyles> = {
  critical: {
    container: "border-red-200 bg-red-50",
    icon: "text-red-600",
    text: "text-red-900",
    button: "text-red-700 hover:bg-red-100 hover:text-red-900",
  },
  warning: {
    container: "border-amber-200 bg-amber-50",
    icon: "text-amber-600",
    text: "text-amber-900",
    button: "text-amber-700 hover:bg-amber-100 hover:text-amber-900",
  },
  info: {
    container: "border-blue-200 bg-blue-50",
    icon: "text-blue-600",
    text: "text-blue-900",
    button: "text-blue-700 hover:bg-blue-100 hover:text-blue-900",
  },
};

/** Resolve the banner Tailwind class groups for a severity (pure, testable). */
export function alertSeverityBannerStyles(
  value: AlertSeverity | string | null | undefined,
): BannerSeverityStyles {
  const raw = value == null ? "" : String(value).trim();
  return (
    ALERT_SEVERITY_BANNER_MAP[raw as AlertSeverity] ??
    ALERT_SEVERITY_BANNER_MAP.info
  );
}

// ---------------------------------------------------------------------------
// Tenant health (placeholder)
// ---------------------------------------------------------------------------

/**
 * Tenant-health bucket. Placeholder enum for C1 (TASK-3881): the cheap
 * derivation lands in Sprint 125 and the real score in Sprint 126, so the
 * color set + labels are defined now and later sprints only supply values.
 */
export type TenantHealth = "healthy" | "at_risk" | "churn_risk" | "new";

const TENANT_HEALTH_MAP: Record<
  string,
  { variant: BadgeVariant; key: string }
> = {
  healthy: { variant: "completed", key: "healthy" },
  at_risk: { variant: "pending", key: "at_risk" },
  churn_risk: { variant: "failed", key: "churn_risk" },
  new: { variant: "planned", key: "new" },
};

/** Resolve `{ variant, label }` for a tenant-health bucket (pure, testable). */
export function tenantHealthBadgeProps(
  value: TenantHealth | string | null | undefined,
  t: TFunction,
): BadgeProps {
  return resolveBadge(
    value,
    TENANT_HEALTH_MAP,
    "adminPanel.badges.tenantHealth",
    t,
  );
}

// ---------------------------------------------------------------------------
// Churn-risk level (Sprint 126, TASK-3902)
// ---------------------------------------------------------------------------

/**
 * Churn-risk level band from the backend churn-risk endpoint (TASK-3901):
 * `low | medium | high`. Maps onto the same semantic Badge variants as tenant
 * health so "at-risk" reads consistently across the panels — high churn risk is
 * the rose `failed` variant (the C1 rose accent for high risk), medium is the
 * amber `pending`, low is the emerald `completed`.
 */
export type ChurnLevel = "low" | "medium" | "high";

const CHURN_LEVEL_MAP: Record<string, { variant: BadgeVariant; key: string }> =
  {
    high: { variant: "failed", key: "high" },
    medium: { variant: "pending", key: "medium" },
    low: { variant: "completed", key: "low" },
  };

/** Resolve `{ variant, label }` for a churn-risk level (pure, testable). */
export function churnLevelBadgeProps(
  value: ChurnLevel | string | null | undefined,
  t: TFunction,
): BadgeProps {
  return resolveBadge(
    value,
    CHURN_LEVEL_MAP,
    "adminPanel.badges.churnLevel",
    t,
  );
}

// ---------------------------------------------------------------------------
// Presentational components
// ---------------------------------------------------------------------------

interface EnumBadgeProps {
  value: string | null | undefined;
  className?: string;
}

/** Render a tenant status as a semantic `Badge`. */
export function TenantStatusBadge({ value, className }: EnumBadgeProps) {
  const { t } = useTranslation();
  const { variant, label } = tenantStatusBadgeProps(value, t);
  return <Badge variant={variant} label={label} className={className} />;
}

/** Render a user role as a semantic `Badge`. */
export function UserRoleBadge({ value, className }: EnumBadgeProps) {
  const { t } = useTranslation();
  const { variant, label } = userRoleBadgeProps(value, t);
  return <Badge variant={variant} label={label} className={className} />;
}

/** Render a billing status as a semantic `Badge`. */
export function BillingStatusBadge({ value, className }: EnumBadgeProps) {
  const { t } = useTranslation();
  const { variant, label } = billingStatusBadgeProps(value, t);
  return <Badge variant={variant} label={label} className={className} />;
}

/** Render an alert severity as a semantic `Badge`. */
export function AlertSeverityBadge({ value, className }: EnumBadgeProps) {
  const { t } = useTranslation();
  const { variant, label } = alertSeverityBadgeProps(value, t);
  return <Badge variant={variant} label={label} className={className} />;
}

/** Render a tenant-health bucket as a semantic `Badge`. */
export function TenantHealthBadge({ value, className }: EnumBadgeProps) {
  const { t } = useTranslation();
  const { variant, label } = tenantHealthBadgeProps(value, t);
  return <Badge variant={variant} label={label} className={className} />;
}

/** Render a churn-risk level as a semantic `Badge`. */
export function ChurnLevelBadge({ value, className }: EnumBadgeProps) {
  const { t } = useTranslation();
  const { variant, label } = churnLevelBadgeProps(value, t);
  return <Badge variant={variant} label={label} className={className} />;
}
